<script setup>
import { ref, computed, watch, inject } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import AddNewRulesInput from 'dashboard/components-next/pilot/assistant/AddNewRulesInput.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['save']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const registerSaveHandler = inject('registerSaveHandler', null);

const uiFlags = useMapGetter('pilotAssistants/getUIFlags');
const isSaving = computed(() => uiFlags.value?.updatingItem);

// 默认配置（仅用于新建助手和补全缺失字段）
const defaultHandoffConfig = {
  // 用户主动触发
  manualTrigger: {
    buttonEnabled: false,
    buttonText: '转人工',
    keywordEnabled: false,
    keywords: [],
  },
  // 转接后续处理
  postTransfer: {
    handoffStartMessage: '正在为您转接人工客服，请稍候...',
  },
};

// 初始状态：全部关闭
const handoffConfig = ref({
  manualTrigger: {
    buttonEnabled: false,
    buttonText: '转人工',
    keywordEnabled: false,
    keywords: [],
  },
  postTransfer: {
    handoffStartMessage: '正在为您转接人工客服，请稍候...',
  },
});

// 从 assistant.config 初始化，真实渲染数据库状态
watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) {
      const existingConfig = newAssistant.config?.handoff || {};

      handoffConfig.value = {
        ...defaultHandoffConfig,
        ...existingConfig,
        manualTrigger: {
          ...defaultHandoffConfig.manualTrigger,
          ...(existingConfig.manualTrigger || {}),
        },
        postTransfer: {
          ...defaultHandoffConfig.postTransfer,
          ...(existingConfig.postTransfer || {}),
          handoffStartMessage:
            existingConfig.postTransfer?.handoffStartMessage ||
            existingConfig.messages?.handoffSuccess ||
            '正在为您转接人工客服，请稍候...',
        },
      };
    }
  },
  { immediate: true }
);

// 添加关键词
const newKeyword = ref('');
const addKeyword = async keyword => {
  const keywordStr = (
    typeof keyword === 'string' ? keyword : newKeyword.value
  ).trim();
  if (keywordStr) {
    const keywords = handoffConfig.value.manualTrigger.keywords;
    if (!keywords.includes(keywordStr)) {
      keywords.push(keywordStr);
    }
    newKeyword.value = '';
    await saveHandoffConfig();
  }
};

const removeKeyword = async index => {
  handoffConfig.value.manualTrigger.keywords.splice(index, 1);
  await saveHandoffConfig();
};

// 跳转到 AI线索 配置
const goToScenarios = () => {
  router.push({
    name: 'pilot_assistant_config_section',
    params: {
      accountId: route.params.accountId,
      assistantId: route.params.assistantId,
      section: 'scenarios',
    },
  });
};

// 恢复默认
const resetToDefault = () => {
  handoffConfig.value = JSON.parse(JSON.stringify(defaultHandoffConfig));
  useAlert('已恢复为默认配置');
};

// 保存按钮名称（失焦时）
const saveButtonText = async () => {
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: {
        config: {
          ...props.assistant.config,
          handoff: handoffConfig.value,
        },
      },
    });
    useAlert(t('PILOT.ASSISTANTS.CONFIG.HANDOFF.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('PILOT.ASSISTANTS.CONFIG.HANDOFF.SAVE_ERROR'));
  }
};

// 保存 handoff 配置（开关切换时）
const saveHandoffConfig = async () => {
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: {
        config: {
          ...props.assistant.config,
          handoff: handoffConfig.value,
        },
      },
    });
    useAlert(t('PILOT.ASSISTANTS.CONFIG.HANDOFF.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('PILOT.ASSISTANTS.CONFIG.HANDOFF.SAVE_ERROR'));
  }
};

// 保存
const saveConfig = async () => {
  // 验证
  if (
    handoffConfig.value.manualTrigger.keywordEnabled &&
    (handoffConfig.value.manualTrigger.keywords?.length || 0) === 0
  ) {
    useAlert('请至少添加一个关键词');
    return false;
  }

  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: {
        config: {
          ...props.assistant.config,
          handoff: handoffConfig.value,
        },
      },
    });
    useAlert(t('PILOT.ASSISTANTS.CONFIG.HANDOFF.SAVE_SUCCESS'));
    return true;
  } catch (error) {
    useAlert(t('PILOT.ASSISTANTS.CONFIG.HANDOFF.SAVE_ERROR'));
    return false;
  }
};

if (registerSaveHandler) {
  registerSaveHandler(saveConfig);
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- 一级标题 -->
    <div class="flex items-center justify-between pt-4">
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ t('PILOT.ASSISTANTS.CONFIG.HANDOFF.TITLE') }}
      </h2>
      <button
        type="button"
        class="flex items-center gap-1 px-3 py-1.5 text-xs text-n-slate-11 hover:text-n-slate-12 border border-n-slate-6 rounded-lg hover:bg-n-slate-1"
        @click="resetToDefault"
      >
        <span class="i-lucide-rotate-ccw" />
        恢复默认
      </button>
    </div>

    <!-- AI线索智能转人工引导卡片 -->
    <div class="scenario-agent-card relative overflow-hidden">
      <div
        class="relative flex items-stretch p-4 gap-4 bg-n-solid-1/95 backdrop-blur-lg"
        style="border-radius: calc(2rem - 2px)"
      >
        <!-- 左侧图标区域 -->
        <div
          class="flex-shrink-0 flex items-center justify-center w-16 h-16 rounded-xl bg-white dark:bg-n-slate-800 shadow-sm"
        >
          <div class="i-woot-captain w-8 h-8 scenario-gradient-icon" />
        </div>

        <!-- 中间内容区域 -->
        <div class="flex-1 min-w-0">
          <h3
            class="text-sm font-semibold text-n-slate-12 dark:text-n-slate-1 mb-1"
          >
            使用「AI线索」实现AI智能转人工
          </h3>
          <p
            class="text-xs text-n-slate-11 dark:text-n-slate-4 mb-3 leading-relaxed"
          >
            AI线索可以自动检测用户对话中的特定意图或情绪，配置「转人工」工具后，就可以自动转接人工客服。比传统的关键词匹配更智能，能理解上下文语义。
          </p>

          <!-- 示例标签 -->
          <div class="flex flex-wrap gap-2 mb-3">
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs"
            >
              <span>😠</span>
              <span>情绪处理专家</span>
            </span>
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs"
            >
              <span>💼</span>
              <span>意向客户转人工</span>
            </span>
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs"
            >
              <span>🤝</span>
              <span>渠道合作引导</span>
            </span>
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs"
            >
              <span>🎁</span>
              <span>新品体验官活动接待员</span>
            </span>
          </div>

          <!-- 跳转按钮 -->
          <button
            type="button"
            class="scenario-gradient-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-all shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
            @click="goToScenarios"
          >
            <span class="i-lucide-git-branch w-3.5 h-3.5" />
            <span>配置AI线索</span>
            <span class="i-lucide-arrow-right w-3 h-3" />
          </button>
        </div>

        <!-- 右侧功能说明 -->
        <div
          class="hidden md:flex flex-col justify-center gap-2 pl-4 border-l border-n-weak"
        >
          <div
            class="flex items-center gap-2 text-xs text-n-slate-10 dark:text-n-slate-5"
          >
            <span class="i-lucide-check-circle w-3.5 h-3.5 text-green-500" />
            <span>自动识别用户情绪</span>
          </div>
          <div
            class="flex items-center gap-2 text-xs text-n-slate-10 dark:text-n-slate-5"
          >
            <span class="i-lucide-check-circle w-3.5 h-3.5 text-green-500" />
            <span>智能判断业务场景</span>
          </div>
          <div
            class="flex items-center gap-2 text-xs text-n-slate-10 dark:text-n-slate-5"
          >
            <span class="i-lucide-check-circle w-3.5 h-3.5 text-green-500" />
            <span>上下文语义理解</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 1. 用户主动转人工 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <h3 class="text-sm font-medium text-n-slate-12 mb-4">用户主动转人工</h3>

      <div>
        <!-- 按钮转人工 -->
        <div class="flex">
          <div class="w-24 flex-shrink-0">
            <span class="text-sm text-n-slate-12">按钮转人工</span>
          </div>
          <div class="flex-1">
            <div class="flex justify-end items-center gap-2">
              <span class="text-xs text-n-slate-10">开启按钮转人工</span>
              <Switch
                v-model="handoffConfig.manualTrigger.buttonEnabled"
                @update:model-value="saveHandoffConfig"
              />
            </div>
            <div v-if="handoffConfig.manualTrigger.buttonEnabled" class="mt-4">
              <label class="text-xs text-n-slate-10 mb-1.5 block">按钮名称</label>
              <input
                v-model="handoffConfig.manualTrigger.buttonText"
                type="text"
                class="w-40 px-3 py-2 text-sm border border-n-slate-6 rounded-lg bg-white dark:bg-n-slate-8"
                @blur="saveButtonText"
              />
            </div>
          </div>
        </div>

        <!-- 关键词转人工 -->
        <div class="flex">
          <div class="w-24 flex-shrink-0">
            <span class="text-sm text-n-slate-12">关键词转人工</span>
          </div>
          <div class="flex-1">
            <div class="flex justify-end items-center gap-2">
              <span class="text-xs text-n-slate-10">开启关键词转人工</span>
              <Switch
                v-model="handoffConfig.manualTrigger.keywordEnabled"
                @update:model-value="saveHandoffConfig"
              />
            </div>
            <div
              v-show="handoffConfig.manualTrigger.keywordEnabled"
              class="mt-4 space-y-4"
            >
              <div>
                <label class="text-xs text-n-slate-10 mb-2 block">触发关键词</label>
                <div
                  v-if="handoffConfig.manualTrigger.keywords?.length"
                  class="flex flex-wrap gap-2 mb-3"
                >
                  <span
                    v-for="(keyword, kIndex) in handoffConfig.manualTrigger
                      .keywords || []"
                    :key="kIndex"
                    class="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-n-slate-8 rounded-md text-xs text-n-slate-11"
                  >
                    {{ keyword }}
                    <button
                      type="button"
                      class="i-lucide-x text-n-slate-9 hover:text-n-slate-11"
                      @click="removeKeyword(kIndex)"
                    />
                  </span>
                </div>
                <AddNewRulesInput
                  v-model="newKeyword"
                  placeholder="输入关键词"
                  label="添加"
                  @add="addKeyword"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 2. 转接后续处理 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <h3 class="text-sm font-medium text-n-slate-12 mb-4">转接后续处理</h3>

      <div class="flex">
        <div class="w-24 flex-shrink-0">
          <span class="text-sm text-n-slate-12">转接提示</span>
        </div>
        <div class="flex-1">
          <label class="text-xs text-n-slate-10 mb-1.5 block">转接开始提示</label>
          <input
            v-model="handoffConfig.postTransfer.handoffStartMessage"
            type="text"
            class="w-full px-3 py-2 text-sm border border-n-slate-6 rounded-lg bg-white dark:bg-n-slate-8"
            @blur="saveHandoffConfig"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* 彩色渐变边框 */
.scenario-agent-card {
  padding: 2px;
  border-radius: 2rem;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%);
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.15);
}

/* 彩色渐变图标 */
.scenario-gradient-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%);
  -webkit-mask-size: cover;
  mask-size: cover;
}

/* 彩色渐变按钮 */
.scenario-gradient-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%);
  border: none;
}

.scenario-gradient-btn:hover {
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #db2777 100%);
}
</style>
